<template>
  <div class="pinyin-converter">
    <h1>汉字转拼音工具</h1>
    
    <div class="converter-container">
      <!-- 输入区域 -->
      <div class="section">
        <div class="section-header">
          <h3>输入文本</h3>
          <div class="actions">
            <el-button size="small" @click="pasteFromClipboard">从剪贴板粘贴</el-button>
            <el-button size="small" @click="clearInput">清空</el-button>
          </div>
        </div>
        <el-input
          v-model="inputText"
          type="textarea"
          :rows="8"
          placeholder="请输入要转换的汉字"
          @input="handleConvert"
        />
      </div>

      <!-- 转换选项 -->
      <div class="options-section">
        <div class="option-group">
          <span>输出格式：</span>
          <el-radio-group v-model="options.format" @change="handleConvert">
            <el-radio-button label="pinyin">拼音</el-radio-button>
            <el-radio-button label="initial">首字母</el-radio-button>
          </el-radio-group>
        </div>

        <div class="option-group">
          <span>分隔符：</span>
          <el-radio-group v-model="options.separator" @change="handleConvert">
            <el-radio-button label=" ">空格</el-radio-button>
            <el-radio-button label="-">连字符</el-radio-button>
            <el-radio-button label="_">下划线</el-radio-button>
            <el-radio-button label="">无分隔</el-radio-button>
          </el-radio-group>
        </div>

        <div class="option-group">
          <el-checkbox v-model="options.toneType" @change="handleConvert">
            显示声调
          </el-checkbox>
          <el-checkbox v-model="options.uppercase" @change="handleConvert">
            大写输出
          </el-checkbox>
          <el-checkbox v-model="options.keepNonChinese" @change="handleConvert">
            保留非中文字符
          </el-checkbox>
        </div>
      </div>

      <!-- 输出区域 -->
      <div class="section">
        <div class="section-header">
          <h3>转换结果</h3>
          <div class="actions">
            <el-button type="primary" size="small" @click="copyResult">复制结果</el-button>
          </div>
        </div>
        <el-input
          v-model="outputText"
          type="textarea"
          :rows="8"
          readonly
          placeholder="转换后的拼音将显示在这里"
        />
      </div>

      <!-- 多音字处理 -->
      <div class="polyphone-section" v-if="polyphones.length > 0">
        <h3>多音字处理</h3>
        <div class="polyphone-list">
          <div
            v-for="(item, index) in polyphones"
            :key="index"
            class="polyphone-item"
          >
            <span class="chinese-char">{{ item.char }}</span>
            <el-radio-group
              v-model="item.selected"
              @change="handlePolyphoneChange"
            >
              <el-radio-button
                v-for="pinyin in item.pinyins"
                :key="pinyin"
                :label="pinyin"
              >
                {{ pinyin }}
              </el-radio-button>
            </el-radio-group>
          </div>
        </div>
      </div>

      <!-- 说明区域 -->
      <div class="info-section">
        <h3>功能说明</h3>
        <ul>
          <li>支持汉字转拼音和首字母</li>
          <li>可选择是否显示声调</li>
          <li>支持自定义分隔符</li>
          <li>支持多音字手动选择读音</li>
          <li>可选择是否保留非中文字符</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { ElMessage } from 'element-plus'
import pinyin from 'pinyin'

const inputText = ref('')
const outputText = ref('')
const polyphones = ref([])

const options = reactive({
  format: 'pinyin',
  separator: ' ',
  toneType: false,
  uppercase: false,
  keepNonChinese: true
})

// 处理转换
const handleConvert = () => {
  if (!inputText.value) {
    outputText.value = ''
    polyphones.value = []
    return
  }

  // 拼音转换选项
  const pinyinOptions = {
    style: options.toneType ? pinyin.STYLE_TONE : pinyin.STYLE_NORMAL,
    segment: true,
    heteronym: true
  }

  // 转换拼音
  const result = pinyin(inputText.value, pinyinOptions)

  // 处理多音字
  polyphones.value = result
    .filter(pys => pys.length > 1)
    .map((pys, index) => ({
      char: inputText.value[index],
      pinyins: pys,
      selected: pys[0]
    }))

  // 格式化输出
  formatOutput(result)
}

// 格式化输出
const formatOutput = (pinyinArray) => {
  let result = pinyinArray.map(pys => {
    // 使用选中的读音或第一个读音
    const py = pys[0]
    
    // 处理首字母模式
    if (options.format === 'initial') {
      return py.charAt(0)
    }
    
    return py
  })

  // 处理大小写
  if (options.uppercase) {
    result = result.map(py => py.toUpperCase())
  }

  // 连接拼音
  outputText.value = result.join(options.separator)
}

// 处理多音字选择变化
const handlePolyphoneChange = () => {
  handleConvert()
}

// 从剪贴板粘贴
const pasteFromClipboard = async () => {
  try {
    const text = await navigator.clipboard.readText()
    inputText.value = text
    handleConvert()
    ElMessage.success('粘贴成功')
  } catch (error) {
    ElMessage.error('无法访问剪贴板')
  }
}

// 清空输入
const clearInput = () => {
  inputText.value = ''
  outputText.value = ''
  polyphones.value = []
}

// 复制结果
const copyResult = async () => {
  if (!outputText.value) {
    ElMessage.warning('没有可复制的内容')
    return
  }

  try {
    await navigator.clipboard.writeText(outputText.value)
    ElMessage.success('复制成功')
  } catch (error) {
    ElMessage.error('复制失败')
  }
}
</script>

<style scoped>
.pinyin-converter {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  margin-top: 80px;
}

.converter-container {
  margin-top: 20px;
}

.section {
  margin: 20px 0;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.actions {
  display: flex;
  gap: 10px;
}

.options-section {
  margin: 20px 0;
  padding: 20px;
  background-color: #f5f7fa;
  border-radius: 8px;
}

.option-group {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 10px 0;
  flex-wrap: wrap;
}

.polyphone-section {
  margin: 20px 0;
  padding: 20px;
  background-color: #f5f7fa;
  border-radius: 8px;
}

.polyphone-list {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 10px;
}

.polyphone-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.chinese-char {
  font-size: 18px;
  font-weight: bold;
}

.info-section {
  margin-top: 30px;
  padding: 20px;
  background-color: #f5f7fa;
  border-radius: 8px;
}

.info-section ul {
  padding-left: 20px;
  margin: 10px 0;
}

.info-section li {
  margin: 8px 0;
  line-height: 1.5;
}

:deep(.el-textarea__inner) {
  font-family: monospace;
  font-size: 14px;
  line-height: 1.5;
}
</style> 